import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { TIDS } from "@/constants/testIds";
import { ShieldCheck, ArrowLeft, Send } from "lucide-react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function AdminPayouts() {
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [pending, setPending] = useState({ rows: [], platform_fee_pct: 10 });
  const [allPayouts, setAllPayouts] = useState([]);
  const [openWs, setOpenWs] = useState(null);
  const [form, setForm] = useState({ amount: "", method: "bank", reference: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: a }, { data: p }, { data: all }] = await Promise.all([
      api.get("/payouts/is-admin"),
      api.get("/admin/payouts/pending"),
      api.get("/admin/payouts/all"),
    ]);
    setAllowed(!!a.is_admin);
    setPending(p);
    setAllPayouts(all);
    setChecking(false);
  };
  useEffect(() => { load().catch(() => { setChecking(false); setAllowed(false); }); }, []);

  const openTrigger = (row) => {
    setOpenWs(row);
    setForm({ amount: String(row.payable_now), method: row.bank?.upi ? "upi" : "bank", reference: "", notes: "" });
  };

  const trigger = async () => {
    if (!openWs || !form.amount || Number(form.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/admin/payouts/trigger", {
        workspace_id: openWs.workspace_id,
        amount: Number(form.amount),
        method: form.method,
        reference: form.reference,
        notes: form.notes,
      });
      toast.success(`Payout of ${inr(form.amount)} recorded for ${openWs.owner_email}`);
      setOpenWs(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  if (checking) return <div className="text-[#4A4D57]">Checking access…</div>;
  if (!allowed) return <Navigate to="/app/payouts" replace />;

  return (
    <div className="space-y-8 vp-fade-up">
      <div>
        <Link to="/app/payouts" className="inline-flex items-center gap-2 text-sm text-[#4A4D57] hover:text-[#0B5CFF]">
          <ArrowLeft className="w-4 h-4" /> My payouts
        </Link>
        <div className="flex items-center gap-2 mt-3">
          <ShieldCheck className="w-5 h-5 text-[#0B5CFF]" />
          <div className="vp-label">Platform admin</div>
        </div>
        <h1 className="font-display text-3xl font-bold mt-1">Payout control center</h1>
        <p className="text-[#4A4D57] mt-2 text-sm max-w-xl">Every creator's payable balance. Trigger manual payouts and record the bank reference here. Platform fee: {pending.platform_fee_pct}%.</p>
      </div>

      <div className="vp-card p-6">
        <div className="vp-label">Pending payouts across all workspaces</div>
        <h2 className="font-display font-bold text-xl mt-1">{pending.rows.length} creator(s) awaiting settlement</h2>
        {pending.rows.length === 0 ? (
          <div className="text-sm text-[#747680] text-center py-8 mt-4">No pending balances. All creators are settled.</div>
        ) : (
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[#747680] uppercase tracking-wide border-b border-[#E4E6EC]">
                  <th className="py-2 pr-3">Creator</th>
                  <th className="py-2 pr-3">Plan</th>
                  <th className="py-2 pr-3 text-right">Lifetime Net</th>
                  <th className="py-2 pr-3 text-right">Paid Out</th>
                  <th className="py-2 pr-3 text-right">Payable Now</th>
                  <th className="py-2 pr-3">Bank</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {pending.rows.map((r) => (
                  <tr key={r.workspace_id} className="border-b border-[#F1F3F7] last:border-0 align-top">
                    <td className="py-3 pr-3">
                      <div className="font-medium">{r.owner_name || "—"}</div>
                      <div className="text-xs text-[#747680]">{r.owner_email}</div>
                    </td>
                    <td className="py-3 pr-3"><span className="vp-chip">{r.plan || "free"}</span></td>
                    <td className="py-3 pr-3 text-right">{inr(r.lifetime_net)}</td>
                    <td className="py-3 pr-3 text-right">{inr(r.paid_out)}</td>
                    <td className="py-3 pr-3 text-right font-semibold text-[#0B5CFF]">{inr(r.payable_now)}</td>
                    <td className="py-3 pr-3 text-xs">
                      {r.has_bank_details ? (
                        <div>
                          <div className="font-medium">{r.bank?.holder_name}</div>
                          <div className="text-[#747680] font-mono">{r.bank?.account_number} · {r.bank?.ifsc}</div>
                          {r.bank?.upi && <div className="text-[#747680] font-mono">UPI · {r.bank.upi}</div>}
                        </div>
                      ) : (
                        <span className="text-[#B00020]">No bank details</span>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-right">
                      <button
                        data-testid={TIDS.adminPayoutTriggerBtn}
                        onClick={() => openTrigger(r)}
                        disabled={!r.has_bank_details}
                        className="vp-btn-primary text-xs inline-flex items-center gap-1 py-1.5 px-3 disabled:opacity-40"
                      >
                        <Send className="w-3.5 h-3.5" /> Trigger payout
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="vp-card p-6">
        <div className="vp-label">All payouts (recent 500)</div>
        <h2 className="font-display font-bold text-xl mt-1">Full platform payout history</h2>
        {allPayouts.length === 0 ? (
          <div className="text-sm text-[#747680] text-center py-8 mt-4">No payouts recorded yet.</div>
        ) : (
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[#747680] uppercase tracking-wide border-b border-[#E4E6EC]">
                  <th className="py-2 pr-3">Paid at</th>
                  <th className="py-2 pr-3">Workspace</th>
                  <th className="py-2 pr-3">Method</th>
                  <th className="py-2 pr-3">Reference</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pr-3">By</th>
                </tr>
              </thead>
              <tbody>
                {allPayouts.map((p) => (
                  <tr key={p.id} className="border-b border-[#F1F3F7] last:border-0">
                    <td className="py-2 pr-3 text-[#4A4D57]">{(p.paid_at || p.created_at || "").slice(0, 10)}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{p.workspace_id.slice(0, 8)}…</td>
                    <td className="py-2 pr-3"><span className="vp-chip">{p.method}</span></td>
                    <td className="py-2 pr-3 font-mono text-xs">{p.reference || "—"}</td>
                    <td className="py-2 pr-3 text-right font-semibold">{inr(p.amount)}</td>
                    <td className="py-2 pr-3 text-xs text-[#747680]">{p.admin_email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openWs && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-5" onClick={() => !busy && setOpenWs(null)}>
          <div className="vp-glass p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display font-bold text-xl">Trigger payout</h2>
            <p className="text-sm text-[#4A4D57] mt-1">Send funds to <b>{openWs.owner_name}</b> ({openWs.owner_email}). Once submitted, this is recorded as PAID immediately.</p>
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Amount (INR)</span>
                <input data-testid={TIDS.adminPayoutAmount} type="number" className="vp-input text-sm" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                <span className="text-xs text-[#747680] mt-1 inline-block">Max payable: {inr(openWs.payable_now)}</span>
              </label>
              <label className="block">
                <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Method</span>
                <select data-testid={TIDS.adminPayoutMethod} className="vp-input text-sm" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                  <option value="bank">Bank NEFT/IMPS</option>
                  <option value="upi">UPI</option>
                  <option value="manual">Manual / Other</option>
                </select>
              </label>
              <label className="block">
                <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Bank/UTR reference</span>
                <input data-testid={TIDS.adminPayoutReference} className="vp-input text-sm" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. UTR12345678" />
              </label>
              <label className="block">
                <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Notes (optional)</span>
                <textarea rows={2} className="vp-input text-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpenWs(null)} disabled={busy} className="vp-btn-ghost text-sm">Cancel</button>
              <button onClick={trigger} disabled={busy} className="vp-btn-primary text-sm">
                {busy ? "Recording…" : "Confirm payout"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
